import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface FormDef {
  id: string;
  name: string;
  slug: string;
  description?: string;
  meta_title?: string;
  meta_description?: string;
  published: number;
  url: string;
  created_at: string;
}

interface FieldDef {
  id: string;
  type: 'text' | 'email' | 'number' | 'textarea' | 'select' | 'checkbox' | 'date';
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
}

export default function Forms() {
  const [forms, setForms] = useState<FormDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingForm, setEditingForm] = useState<FormDef | null>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [published, setPublished] = useState(0);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [saving, setSaving] = useState(false);
  const [builderError, setBuilderError] = useState('');

  useEffect(() => { loadForms(); }, []);

  async function loadForms() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/forms');
      setForms(res.data ?? []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function newForm() {
    setShowBuilder(true);
    setEditingForm(null);
    setName('');
    setSlug('');
    setDescription('');
    setMetaTitle('');
    setMetaDesc('');
    setPublished(0);
    setFields([]);
  }

  function editForm(f: FormDef) {
    setShowBuilder(true);
    setEditingForm(f);
    setName(f.name);
    setSlug(f.slug);
    setDescription(f.description ?? '');
    setMetaTitle(f.meta_title ?? '');
    setMetaDesc(f.meta_description ?? '');
    setPublished(f.published);
    setFields([]);
  }

  async function saveForm() {
    setBuilderError('');
    if (!name.trim() || !slug.trim()) {
      setBuilderError('Name and slug are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name, slug, description: description || null,
        meta_title: metaTitle || null, meta_description: metaDesc || null,
        published: published ? 1 : 0,
        content_json: fields,
      };
      if (editingForm) {
        await api.put(`/api/forms/${editingForm.id}`, payload);
      } else {
        await api.post('/api/forms', payload);
      }
      setShowBuilder(false);
      loadForms();
    } catch (err: any) {
      setBuilderError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function deleteForm(id: string) {
    if (!confirm('Delete this form?')) return;
    await api.del(`/api/forms/${id}`);
    loadForms();
  }

  async function togglePublished(f: FormDef) {
    await api.put(`/api/forms/${f.id}`, { published: f.published ? 0 : 1 });
    loadForms();
  }

  function addField(type: FieldDef['type']) {
    setFields([...fields, { id: crypto.randomUUID(), type, label: 'New Field', required: false }]);
  }

  function updateField(id: string, key: keyof FieldDef, value: any) {
    setFields(fields.map((f) => f.id === id ? { ...f, [key]: value } : f));
  }

  function removeField(id: string) { setFields(fields.filter((f) => f.id !== id)); }
  function moveField(id: string, dir: -1 | 1) {
    const idx = fields.findIndex((f) => f.id === id);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= fields.length) return;
    const arr = [...fields];
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    setFields(arr);
  }

  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;

  if (showBuilder) {
    return (
      <div style={{ maxWidth: 960 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{editingForm ? 'Edit' : 'New'} Form</h1>
          <button onClick={() => setShowBuilder(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {builderError && <div style={{ background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{builderError}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          <input placeholder="Form name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          <div style={{ display: 'flex', gap: 10 }}>
            <input placeholder="Slug" value={slug} onChange={(e) => setSlug(e.target.value)} style={inputStyle} />
            <select value={published} onChange={(e) => setPublished(parseInt(e.target.value))} style={inputStyle}>
              <option value={0}>Draft</option>
              <option value={1}>Published</option>
            </select>
          </div>
          <input placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
          <input placeholder="Meta title (SEO)" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} style={inputStyle} />
          <input placeholder="Meta description (SEO)" value={metaDesc} onChange={(e) => setMetaDesc(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {(['text','email','number','textarea','select','checkbox','date'] as FieldDef['type'][]).map((t) => (
            <button key={t} onClick={() => addField(t)} style={pillBtn}>{`+ ${t}`}</button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {fields.map((field) => (
            <div key={field.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>{field.type}</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => moveField(field.id, -1)} style={iconBtn}>↑</button>
                  <button onClick={() => moveField(field.id, 1)} style={iconBtn}>↓</button>
                  <button onClick={() => removeField(field.id)} style={iconBtn}>×</button>
                </div>
              </div>
              <input value={field.label} onChange={(e) => updateField(field.id, 'label', e.target.value)} placeholder="Label" style={inputStyle} />
              <input value={field.placeholder ?? ''} onChange={(e) => updateField(field.id, 'placeholder', e.target.value)} placeholder="Placeholder" style={inputStyle} />
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <input type="checkbox" checked={field.required ?? false} onChange={(e) => updateField(field.id, 'required', e.target.checked)} />
                Required
              </label>
              {field.type === 'select' && (
                <input value={field.options?.join(', ') ?? ''} onChange={(e) => updateField(field.id, 'options', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} placeholder="Options, comma separated" style={inputStyle} />
              )}
            </div>
          ))}
          {fields.length === 0 && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40, border: '1px dashed #e5e7eb', borderRadius: 12 }}>No fields yet. Click a button above to add.</div>}
        </div>

        <button onClick={saveForm} disabled={saving} style={{ padding: '10px 24px', fontSize: 14, fontWeight: 500, background: '#111827', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
          {saving ? 'Saving...' : 'Save Form'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Forms</h1>
        <button onClick={newForm} style={styles.createBtn}>+ New Form</button>
      </div>
      {error && <div style={styles.error}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
        {forms.map((f) => (
          <div key={f.id} style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>{f.name}</h3>
              <span style={styles.badge(publishedColor(f.published))}>{f.published ? 'Published' : 'Draft'}</span>
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>/{f.slug}</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{f.url ?? 'No URL'}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => editForm(f)} style={styles.smallBtn}>Edit</button>
              <button onClick={() => togglePublished(f)} style={styles.smallBtn}>{f.published ? 'Unpublish' : 'Publish'}</button>
              <button onClick={() => deleteForm(f.id)} style={{ ...styles.smallBtn, color: '#dc2626' }}>Delete</button>
            </div>
          </div>
        ))}
        {forms.length === 0 && <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#9ca3af', padding: 60 }}>No forms yet.</div>}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = { padding: '8px 10px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: 'inherit', display: 'block', width: '100%', marginBottom: 6 };
const pillBtn: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '4px 12px', fontSize: 12, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { width: 24, height: 24, borderRadius: 4, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, lineHeight: 1 };

function publishedColor(p: number) { return p ? { bg: '#dcfce7', text: '#16a34a' } : { bg: '#f3f4f6', text: '#6b7280' }; }
const styles = {
  createBtn: { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' } as React.CSSProperties,
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 } as React.CSSProperties,
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 } as React.CSSProperties,
  badge: (c: { bg: string; text: string }) => ({ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: c.bg, color: c.text } as React.CSSProperties),
  smallBtn: { fontSize: 12, background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' } as React.CSSProperties,
};
